import { useMemo } from "react";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import { uniqBy } from "diagnostics-lib/array";
import { computeFaultImpactOverPeriod } from "diagnostics-domain/aggregationPeriod/faultValueOverPeriod";
import { findPowerQuantityId } from "diagnostics-domain/aggregationPeriod";
import type { Quantity } from "../../../../api/useQuantities";
import { indexBy } from "diagnostics-lib/iterable";
import type { Event } from "../../../../api/useEvents";
import { getManualFaultImpactsByAnalysis } from "diagnostics-domain/manualFaultEvent";
import { isFailureValueInFaultEventOccurrencePeriods } from "diagnostics-domain/faultEvent";
interface Props {
  indexedFailureValues: IndexedFailureValues;
  quantities: Quantity[];
  events: Event[];
  aggregationInterval: { from: Date; to: Date };
  activeAnalyseDatesPeriod: number[];
}
export const NO_SYSTEM = "no-system";
const useLossesBySystem = ({
  indexedFailureValues,
  quantities,
  events,
  aggregationInterval,
  activeAnalyseDatesPeriod,
}: Props) => {
  const indexedQuantities = useMemo(() => indexBy(quantities, "id"), [quantities]);
  const failureValues = useMemo(
    () =>
      uniqBy(
        Object.values(indexedFailureValues)
          .flatMap(failureValues =>
            Object.values(failureValues).map(fv => ({
              date: fv.date,
              failureName: fv.failureName,
              real: fv.impacts,
            })),
          )
          .filter(
            fv =>
              fv.date >= aggregationInterval.from.getTime() &&
              fv.date <= aggregationInterval.to.getTime(),
          ),
        f => `${f.failureName}-${f.date}`,
      ),
    [aggregationInterval, indexedFailureValues],
  );

  const meanAverageLossesByEvent = useMemo(
    () =>
      events.flatMap(event => {
        const powerQuantityId = findPowerQuantityId(quantities);
        const powerQuantity = powerQuantityId
          ? (indexedQuantities.get(powerQuantityId) ?? null)
          : null;

        switch (event.type) {
          case "DIAGNOSED":
            return event.metadata.faults.map(fault => {
              const faultValuesOverPeriod = failureValues.filter(
                fv =>
                  fv.failureName === fault.name &&
                  isFailureValueInFaultEventOccurrencePeriods(event, {
                    projectId: event.projectId,
                    ...fv,
                  }) &&
                  fv.date >= aggregationInterval.from.getTime() &&
                  fv.date <= aggregationInterval.to.getTime(),
              );

              const faultStatusOverPeriod = fault.statusByAnalysis.map(status => ({
                ...status,
                failureName: fault.name,
              }));

              const meanImpact = computeFaultImpactOverPeriod(
                powerQuantity?.id ?? null,
                activeAnalyseDatesPeriod,
                faultValuesOverPeriod,
                faultStatusOverPeriod,
              );

              return {
                system: fault.systemId,
                mean: meanImpact,
              };
            });

          case "MANUAL": {
            const impactsOverPeriod = getManualFaultImpactsByAnalysis({
              manualFaultEvent: {
                failureName: event.metadata.manualFault.name,
                windows: event.windows,
                impacts: event.impacts,
                endsAt: event.metadata.endsAt,
              },
              activeAnalyseDatesPeriod,
              performanceIndicators: powerQuantity ? [powerQuantity] : [],
            });

            const meanImpact = computeFaultImpactOverPeriod(
              powerQuantity?.id ?? null,
              activeAnalyseDatesPeriod,
              impactsOverPeriod,
              [],
            );

            return [
              {
                system: event.metadata.manualFault.systemId,
                mean: meanImpact,
              },
            ];
          }

          default:
            throw new Error(event satisfies never);
        }
      }),
    [
      events,
      failureValues,
      aggregationInterval,
      quantities,
      indexedQuantities,
      activeAnalyseDatesPeriod,
    ],
  );

  const lossesSystems = useMemo(
    () => computeSortedLossesBySystem(meanAverageLossesByEvent),
    [meanAverageLossesByEvent],
  );

  return lossesSystems;
};

export default useLossesBySystem;

export const computeSortedLossesBySystem = (
  losses: { system: string | null; mean: number | null }[],
) => {
  const lossesBySystem = new Map<string, number>();

  for (const { system, mean } of losses) {
    if (mean === null || mean === 0) continue;
    const key = system ?? NO_SYSTEM;
    lossesBySystem.set(key, (lossesBySystem.get(key) ?? 0) + mean);
  }

  return Array.from(lossesBySystem.entries())
    .map(([system, averageLosses]) => ({ system, averageLosses }))
    .sort((a, b) => {
      if (a.system === NO_SYSTEM) return 1;
      if (b.system === NO_SYSTEM) return -1;
      return a.averageLosses - b.averageLosses;
    });
};
